import {
  ComponentProps,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { LayoutChangeEvent } from "react-native";
import type { WithSpringConfig } from "react-native-reanimated";
import Animated, {
  ReduceMotion,
  SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
} from "react-native-reanimated";
import { Separator, styled, Text, View, ViewProps, XStack } from "tamagui";

import { stylex } from "../../../utils";
import { Accordion } from "../../ui/accordion";
import { Card } from "../../ui/card";
import { Score } from "../../ui/score";
import { StatCard as StatCardBase } from "../../ui/stat-card";
import { SwitchWithLabel } from "../../ui/switch";

interface ScaleCardContainerContext {
  toggle: (id: string | undefined) => void;
  openCards: string[];
}

interface ScaleCardContext {
  height: SharedValue<number>;
  id: string | undefined;
  danger: boolean;
}

const ScaleCardContainerContext =
  createContext<ScaleCardContainerContext | null>(null);

export const ScaleCardContext = createContext<ScaleCardContext | null>(null);

export const useScaleCardContainerContext = () => {
  const context = useContext(ScaleCardContainerContext);
  if (!context) {
    throw new Error(
      "useScaleCardContainerContext must be used within an ScaleCardContainerContext",
    );
  }
  return context;
};

export const useScaleCardContext = () => {
  const context = useContext(ScaleCardContext);
  if (!context) {
    throw new Error(
      "useScaleCardContext must be used within an Body component",
    );
  }
  return context;
};

export const ScaleCardContainerProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [openCards, setOpenCards] = useState<string[]>([]);

  const toggle = useCallback((id: string | undefined) => {
    if (!id) return console.warn("ID is undefined");

    setOpenCards((prev) => {
      if (prev.includes(id)) return prev.filter((item) => item !== id);
      return [...prev, id];
    });
  }, []);

  return (
    <ScaleCardContainerContext.Provider value={{ openCards, toggle }}>
      {children}
    </ScaleCardContainerContext.Provider>
  );
};

export type ExpandableAreaProps = React.ComponentProps<typeof Animated.View>;

export const ExpandableArea: React.FC<ExpandableAreaProps> = ({
  children,
  ...rest
}) => {
  const { openCards } = useScaleCardContainerContext();
  const { id, height } = useScaleCardContext();
  const open = id ? openCards.includes(id) : false;

  const [springConfig] = useState<WithSpringConfig>({
    damping: 100,
    stiffness: 200,
    reduceMotion: ReduceMotion.Never,
  });

  const animatedExpandableArea = useAnimatedStyle(() => ({
    height: open
      ? withDelay(10, withSpring(height.value, springConfig))
      : withDelay(10, withSpring(0, springConfig)),
  }));

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      height.value = event.nativeEvent.layout.height;
    },
    [height],
  );

  const [display, setDisplay] = useState(true);

  useEffect(() => {
    if (open) setDisplay(true);
    else setTimeout(() => setDisplay(false), 300);
  }, [open]);

  return (
    <Animated.View
      style={[{ overflow: "hidden" }, animatedExpandableArea]}
      {...rest}
    >
      {display && <View onLayout={handleLayout}>{children}</View>}
    </Animated.View>
  );
};

const StatsContainer = styled(XStack, {
  name: "StatsContainer",
  alignItems: "center",
  justifyContent: "space-between",
});

const Title = styled(Text, {
  name: "Title",
  ellipsizeMode: "tail",
  width: "100%",
  flexShrink: 1,
  variants: {
    variant: {
      default: {
        fontFamily: "$subtitle",
        color: "$gray-primary-900",
        textAlign: "center",
      },
    },
    font: {
      lg: {
        fontWeight: "$6",
        fontSize: "$5",
        lineHeight: "$5",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
    font: "lg",
  },
});

const ScaleState = styled(Text, {
  name: "ScaleState",
  textAlign: "left",
  variants: {
    variant: {
      default: {
        fontFamily: "$subtitle",
        color: "$gray-primary-900",
      },
    },
    state: {
      success: {
        color: "$score-8",
      },
      warning: {
        color: "$warning-500",
      },
      neutral: {
        color: "$gray-600",
      },
      critical: {
        color: "$score-2-1",
      },
      danger: {
        color: "$error-500",
      },
    },
    font: {
      lg: {
        fontWeight: "$5",
        fontSize: "$4",
        lineHeight: "$4",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
    font: "lg",
    state: "neutral",
  },
});

type ScoreState = React.ComponentProps<typeof Score>["variant"];

const Header: React.FC<{
  score: string | number | null | undefined;
  title: string | null | undefined;
  state: ScoreState;
  stateText: string | null | undefined;
  toogleLabel: string | undefined;
}> = ({ score, state, title, stateText, toogleLabel }) => {
  const { openCards } = useScaleCardContainerContext();
  const { id } = useScaleCardContext();
  const open = id ? openCards.includes(id) : false;

  const animatedExpandableArea = useAnimatedStyle(() => ({
    width: "100%",
    ...stylex(
      [
        !open,
        {
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: 4,
          alignItems: "center",
        },
      ],
      [
        open,
        {
          flexDirection: "row",
          justifyContent: "space-between",
        },
      ],
    ),
  }));

  return (
    <Animated.View style={[animatedExpandableArea]}>
      <View
        data-id="scale-card-header"
        style={{
          flexDirection: "column-reverse",
          alignItems: "center",
          gap: 20,
          ...stylex([!open, { width: "100%" }]),
          ...stylex([open, { flex: 1, flexShrink: 1, flexDirection: "row" }]),
        }}
      >
        <View
          style={{
            flexShrink: 1,
            flexDirection: open ? "row" : "column",
            alignItems: "center",
            gap: open ? 20 : 16,
          }}
        >
          <Score
            data-id={"scale-score"}
            data-value={score}
            size={open ? "md" : "lg"}
            variant={state}
          >
            {score}
          </Score>
          <Title
            data-id={"scale-title"}
            data-value={title}
            numberOfLines={open ? 1 : 3}
          >
            {title}
          </Title>
        </View>
        {!open ? (
          <View
            width={"100%"}
            flex={1}
            height={"$0.2"}
            backgroundColor={"$gray-200"}
          />
        ) : null}

        {open ? (
          <ScaleState
            ellipsizeMode="tail"
            numberOfLines={1}
            data-id={"scale-state-description"}
            data-value={stateText}
            state={state}
          >
            {stateText}
          </ScaleState>
        ) : (
          <Toogle label={toogleLabel} />
        )}
      </View>

      {!open ? (
        <ScaleState
          ellipsizeMode="tail"
          numberOfLines={1}
          data-id={"scale-state-description"}
          data-value={stateText}
          state={state}
        >
          {stateText}
        </ScaleState>
      ) : (
        <Toogle label={toogleLabel} />
      )}
    </Animated.View>
  );
};

const ManagerHeader: React.FC<{
  score: string | number | null | undefined;
  title: string | null | undefined;
  state: ScoreState;
  stateText: string | null | undefined;
  toogleLabel: string | undefined;
  children?: React.ReactNode;
}> = ({ state, title, stateText, toogleLabel, children }) => {
  const { openCards } = useScaleCardContainerContext();
  const { id, danger } = useScaleCardContext();
  const open = id ? openCards.includes(id) : false;

  return (
    <View
      flex={1}
      width={"100%"}
      data-id="scale-card-header"
      gap={open ? "$4" : "$2"}
    >
      <XStack
        justifyContent="space-between"
        alignItems="center"
        flex={1}
        gap={"$1.5"}
      >
        <XStack
          justifyContent="flex-start"
          alignItems="center"
          flex={1}
          gap={"$1.5"}
        >
          <Title
            {...(danger && { color: "$error-500" })}
            numberOfLines={1}
            textAlign="left"
            width={"auto"}
            data-id={"scale-title"}
          >
            {title}
          </Title>
          {stateText ? (
            <>
              <Separator
                vertical
                height={"$3"}
                borderRightColor={"$gray-200"}
              />
              <ScaleState
                flexGrow={1}
                textWrap="nowrap"
                whiteSpace="nowrap"
                state={state}
              >
                {stateText}
              </ScaleState>
            </>
          ) : null}
        </XStack>

        <Toogle label={toogleLabel} />
      </XStack>
      {children}
    </View>
  );
};

type StatCardProps = ComponentProps<typeof StatCardBase.Body> & {
  score: string | number | null | undefined;
  description: string | null | undefined;
};

const StatCard: React.FC<StatCardProps> = ({ score, description, ...rest }) => {
  const { openCards: items } = useScaleCardContainerContext();
  const { id, danger } = useScaleCardContext();
  const open = id ? items.includes(id) : false;

  return (
    <StatCardBase.Body
      data-id={"scale-stat-card"}
      {...(danger && {
        backgroundColor: "$bgpeach-500",
      })}
      size={open ? "lg" : "md"}
      {...rest}
    >
      <StatCardBase.Score
        data-id={"scale-stat-card-score"}
        {...(danger && {
          color: "$error-500",
        })}
      >
        {score}
      </StatCardBase.Score>
      <StatCardBase.Description data-id={"scale-stat-card-description"}>
        {description}
      </StatCardBase.Description>
    </StatCardBase.Body>
  );
};

const Toogle: React.FC<{ label: string | undefined }> = ({ label }) => {
  const { toggle, openCards: items } = useScaleCardContainerContext();
  const { id } = useScaleCardContext();
  const open = id ? items.includes(id) : false;

  return (
    <View
      data-id={"scale-toggle"}
      height={"$5"}
      justifyContent="center"
      alignItems="center"
      backgroundColor={"$gray-50"}
      padding={"$3.4"}
      borderRadius={"$8"}
    >
      <SwitchWithLabel
        isChecked={open}
        onCheckedChange={() => toggle(id)}
        label={label}
      />
    </View>
  );
};

const ScaleCardContainer: React.FC<ViewProps> = ({ children, ...rest }) => {
  return (
    <View flex={1} flexDirection="column" gap={"$3"} width="100%" {...rest}>
      {children}
    </View>
  );
};

type ScaleCardBodyProps = React.ComponentProps<typeof Card> & {
  id: NonNullable<string>;
  danger?: boolean;
};

const ScaleCardBody: React.FC<ScaleCardBodyProps> = ({
  children,
  id,
  danger = false,
  ...rest
}) => {
  const height = useSharedValue(0);

  return (
    <ScaleCardContext.Provider value={{ id, height, danger }}>
      <Card
        flex={1}
        paddingVertical={"$2"}
        paddingHorizontal={"$5"}
        {...(danger && { borderColor: "$error-500" })}
        {...rest}
      >
        {children}
      </Card>
    </ScaleCardContext.Provider>
  );
};

export const Scale = {
  Provider: ScaleCardContainerProvider,
  Container: ScaleCardContainer,
  ExpandableArea,
  StatCard,
  Body: ScaleCardBody,
  Card,
  StatsContainer,
  Header,
  Accordion,
  ManagerHeader,
  State: ScaleState,
};
